import { useEffect, useState, useCallback } from 'react';
import { toast } from '../../../../../../../ui/toast';
import {
  useGetPreQualConfigQuery,
  useUpdatePreQualQuestionsMutation,
} from '../../../../../../../utils/api_service/preQualApi';
import { PreQualQuestion } from '../../../../../../../utils/types/JobOpenings';
import {
  IUsePreQualifyingQuestionsReturn,
  PreQualQuestionType,
} from '../interfaces';
import { generateUniqueKey, mapBankQuestionTypeToPreQualType } from '../functions';
import { DEFAULT_QUESTION_TYPE } from '../constants';

export const usePreQualifyingQuestions = (
  jobOpeningId: string,
): IUsePreQualifyingQuestionsReturn => {
  const { data: config, isLoading } = useGetPreQualConfigQuery(jobOpeningId, {
    skip: !jobOpeningId,
  });
  const [updateQuestions] = useUpdatePreQualQuestionsMutation();

  const [questions, setQuestions] = useState<PreQualQuestion[]>([]);
  const [newQuestionType, setNewQuestionType] =
    useState<PreQualQuestionType>(DEFAULT_QUESTION_TYPE);
  const [newQuestionText, setNewQuestionText] = useState<string>('');
  const [choices, setChoices] = useState<string[]>(['']);

  const [editQuestionType, setEditQuestionType] =
    useState<PreQualQuestionType>(DEFAULT_QUESTION_TYPE);
  const [editQuestionText, setEditQuestionText] = useState<string>('');
  const [editChoices, setEditChoices] = useState<string[]>(['']);
  const [editIndex, setEditIndex] = useState<string | null>(null);

  useEffect(() => {
    if (config?.questions) {
      const repairedQuestions = config.questions.map((q) => ({
        ...q,
        questionKey:
          q.questionKey && q.questionKey !== ''
            ? q.questionKey
            : generateUniqueKey(),
      }));
      setQuestions(repairedQuestions);
    }
  }, [config]);

  const handleAddQuestion = useCallback(async () => {
    if (!newQuestionText.trim()) return;

    const filteredChoices = choices.filter((choice) => choice.trim() !== '');
    const newQuestion: PreQualQuestion = {
      questionKey: generateUniqueKey(),
      questionType: newQuestionType,
      question: newQuestionText.trim(),
      choices:
        newQuestionType === 'multiple-choice' ? [...filteredChoices] : [],
      order: questions.length,
    };

    const updatedQuestions = [...questions, newQuestion];

    try {
      await updateQuestions({
        jobOpeningId,
        questions: updatedQuestions,
      }).unwrap();
      setNewQuestionText('');
      setChoices(['']);
      toast.success('Question added successfully');
    } catch (error) {
      toast.error('Failed to add question');
    }
  }, [newQuestionText, choices, newQuestionType, questions, updateQuestions, jobOpeningId]);

  const handleChoiceChange = useCallback(
    (index: number, value: string, edit: boolean) => {
      if (edit) {
        setEditChoices((prev) => {
          const next = [...prev];
          next[index] = value;
          return next;
        });
      } else {
        setChoices((prev) => {
          const next = [...prev];
          next[index] = value;
          return next;
        });
      }
    },
    [],
  );

  const handleDeleteQuestion = useCallback(
    async (key: string) => {
      const updatedQuestions = questions
        .filter((q) => q.questionKey !== key)
        .map((q, index) => ({ ...q, order: index }));

      try {
        await updateQuestions({
          jobOpeningId,
          questions: updatedQuestions,
        }).unwrap();
        toast.info('Question removed');
      } catch (error) {
        toast.error('Failed to remove question');
      }
    },
    [questions, updateQuestions, jobOpeningId],
  );

  const handleSaveQuestion = useCallback(async () => {
    if (editIndex !== null) {
      const updatedQuestions = questions.map((question) => {
        if (question.questionKey === editIndex) {
          return {
            ...question,
            question: editQuestionText.trim(),
            questionType: editQuestionType,
            choices:
              editQuestionType === 'multiple-choice'
                ? editChoices.filter((choice) => choice.trim() !== '')
                : [],
          };
        }
        return question;
      });

      try {
        await updateQuestions({
          jobOpeningId,
          questions: updatedQuestions,
        }).unwrap();
        setEditIndex(null);
        setEditQuestionText('');
        setEditChoices(['']);
        toast.success('Question updated');
      } catch (error) {
        toast.error('Failed to update question');
      }
    }
  }, [
    editIndex,
    questions,
    editQuestionText,
    editQuestionType,
    editChoices,
    updateQuestions,
    jobOpeningId,
  ]);

  const handleEditQuestion = useCallback((question: PreQualQuestion) => {
    if (question.questionKey) {
      setEditIndex(question.questionKey);
      setEditQuestionText(question.question);
      setEditQuestionType(question.questionType);
      setEditChoices(question.choices.length > 0 ? question.choices : ['']);
    }
  }, []);

  const handleAddFromBank = useCallback(
    async (bankQuestion: {
      question: string;
      questionType:
        | 'numeric'
        | 'multiple-choice'
        | 'location'
        | 'text'
        | 'video-answer'
        | 'video';
      choices?: string[];
    }) => {
      if (bankQuestion.questionType === 'text') {
        toast.error('Text questions are not supported in pre-qualifying questions');
        return;
      }

      const questionType = mapBankQuestionTypeToPreQualType(
        bankQuestion.questionType,
      );

      const newQuestion: PreQualQuestion = {
        questionKey: generateUniqueKey(),
        question: bankQuestion.question,
        questionType,
        choices: bankQuestion.choices || [],
        order: questions.length,
      };

      const updatedQuestions = [...questions, newQuestion];

      try {
        await updateQuestions({
          jobOpeningId,
          questions: updatedQuestions,
        }).unwrap();
        toast.success('Question imported from bank');
      } catch (error) {
        toast.error('Failed to import question');
      }
    },
    [questions, updateQuestions, jobOpeningId],
  );

  return {
    choices,
    editChoices,
    setNewQuestionType,
    handleSaveQuestion,
    handleEditQuestion,
    handleDeleteQuestion,
    handleChoiceChange,
    handleAddQuestion,
    handleAddFromBank,
    setNewQuestionText,
    setEditChoices,
    editQuestionType,
    setEditQuestionType,
    editQuestionText,
    setEditQuestionText,
    editIndex,
    questions,
    newQuestionText,
    newQuestionType,
    setChoices,
    setEditIndex,
    isLoading,
  };
};
